import React, { useEffect, useMemo, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { toast } from "sonner";
import { num, money } from "../lib/holdingsMath";
import { HOLDINGS_SUBSECTIONS } from "../lib/holdingsTaxonomy";
import { Trash2, Plane, Ship, Car, Bike, Building2 } from "lucide-react";
import { AttachmentsButton } from "./Attachments";
import { AppraisalButton } from "./Appraisal";

const MOB_CLASS2 = HOLDINGS_SUBSECTIONS["Mobility Property"];
const STATUSES = ["Personal", "Rental", "Idle", "Refurbish"];
const MOB_INTENTS = ["Sell", "Sail", "Sit"];
const FREQS = { Annual: 1, Quarterly: 4, Monthly: 12 };
const II = "w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-[11px] dark:border-white/15 dark:bg-black/30";
const LL = "text-[9px] uppercase tracking-wide opacity-55";
const nowYear = new Date().getFullYear();

const iconFor = (cls, category) => {
  if (category === "Real Estate") return Building2;
  if (["Vessels", "Seacraft"].includes(cls)) return Ship;
  if (["Vintage cars", "Muscle cars"].includes(cls)) return Car;
  if (cls === "Bikes") return Bike;
  return Plane; // Aircraft, Rotary and Minicraft
};

const bookValueOf = (r) => {
  const appraised = num(r.last_appraised);
  const yb = num(r.year_bought);
  const yrs = yb ? Math.max(0, nowYear - yb) : 0;
  return Math.max(0, appraised - num(r.annual_dep) * yrs);
};
const netOf = (r) => num(r.income_annual) - num(r.annual_outlay);
const sliceOf = (r, key) => num(r[key]) / (FREQS[r.settle_freq] || 1);

function Field({ label, children }) {
  return <label className="block"><span className={LL}>{label}</span>{children}</label>;
}

export default function MobilityLedger({ items = [], reload, category = "Mobility Property", class2Options = MOB_CLASS2, intents = MOB_INTENTS, madeLabel = "Made", p = "mob", scope = null }) {
  const [edits, setEdits] = useState({});
  const [yearMode, setYearMode] = useState({});
  const [pools, setPools] = useState([]);

  const loadPools = () => client.get("/currencies").then((r) => setPools((r.data || []).map((x) => ({ id: x.id, ...(x.data || x) })))).catch(() => {});
  useEffect(() => { loadPools(); }, []);

  const adjust = async (poolId, direction, amount, reason, meta) => {
    if (!poolId) return toast.error("Pick a Currencida account first.");
    if (!(Number(amount) > 0)) return toast.error("Nothing to move — amount is zero.");
    try {
      const r = await client.post("/currency/adjust", { pool_id: poolId, direction, amount: Number(amount), reason, meta: meta || {} });
      toast.success(`${direction === "debit" ? "Debited" : "Credited"} ${money(amount)} · new balance ${money(r.data.balance)}.`);
      loadPools();
    } catch (e) { toast.error(e?.response?.data?.detail || "Settle failed."); }
  };

  useEffect(() => {
    setEdits((prev) => {
      const ids = new Set(items.map((i) => i.id));
      const next = {};
      Object.keys(prev).forEach((k) => { if (ids.has(k)) next[k] = prev[k]; });
      return next;
    });
  }, [items]);

  const view = (r) => ({ ...r, ...(edits[r.id] || {}) });
  const setField = (id, key, value) => setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));
  const persist = (id, key) => {
    const value = (edits[id] || {})[key];
    client.put(`/holdings/${id}`, { data: { [key]: value } }).catch(() => toast.error("Could not save."));
  };
  const persistNow = (id, key, value) => {
    setField(id, key, value);
    client.put(`/holdings/${id}`, { data: { [key]: value } }).then(() => reload && reload()).catch(() => toast.error("Could not save."));
  };

  const addSlots = async (count) => {
    try {
      const defSub = Array.isArray(class2Options) && class2Options.length ? class2Options[0] : "";
      for (let i = 0; i < count; i++) {
        await client.post("/holdings", { data: { category, subcategory: defSub, asset_class: "Other", name: "", ...(scope || {}) } });
      }
      toast.success(`Added ${count} blank ${count > 1 ? "rows" : "row"}.`);
      reload && reload();
    } catch (e) { toast.error("Could not add rows."); }
  };
  const del = async (r) => {
    if (!window.confirm(`Remove ${r.name || "this item"}?`)) return;
    try { await client.delete(`/holdings/${r.id}`); toast.success("Removed."); reload && reload(); }
    catch (e) { toast.error("Delete failed."); }
  };

  const totals = useMemo(() => {
    let book = 0, net = 0;
    items.forEach((r) => { const v = view(r); book += bookValueOf(v); net += netOf(v); });
    return { book, net };
  }, [items, edits]); // eslint-disable-line

  return (
    <div className="space-y-3" data-testid={`${p}-ledger`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide opacity-55">Add {category === "Real Estate" ? "property" : "mobility asset"}:</span>
        <div className="flex overflow-hidden rounded border border-black/15 text-[11px] dark:border-white/15">
          {[1, 5, 10].map((c) => (
            <button key={c} onClick={() => addSlots(c)} data-testid={`${p}-add-${c}`} className="px-2 py-1 font-semibold hover:bg-[var(--primary)] hover:text-white" title={`Add ${c} blank row${c > 1 ? "s" : ""}`}>+{c}</button>
          ))}
        </div>
        <div className="ms-auto flex items-center gap-4 text-[11px]">
          <span>{TL("Total book value")} <b className="tabular-nums" data-testid={`${p}-total-book`}>{money(totals.book)}</b></span>
          <span>{TL("Total net")} <b className={`tabular-nums ${totals.net >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid={`${p}-total-net`}>{totals.net >= 0 ? "+" : "−"}{money(Math.abs(totals.net))}</b></span>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-black/15 py-6 text-center text-xs opacity-60 dark:border-white/15" data-testid={`${p}-empty`}>
          No {category === "Real Estate" ? "properties" : "aircraft or vessels"} yet. Use the “+1 / +5 / +10” buttons to create rows, then fill them in inline.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((raw) => {
            const r = view(raw);
            const ym = yearMode[r.id] || "made";
            const yearKey = ym === "made" ? "year_made" : "year_bought";
            const otherKey = ym === "made" ? "year_bought" : "year_made";
            const Icon = iconFor(r.subcategory, category);
            return (
              <div key={r.id} className="rounded-lg border border-black/10 bg-white/50 p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`${p}-row-${r.id}`}>
                {/* ===== Upper line ===== */}
                <div className="grid items-end gap-2 md:grid-cols-7">
                  <Field label={TL("Main class")}>
                    <input className={II} value={r.name || ""} onChange={(e) => setField(r.id, "name", e.target.value)} onBlur={() => persist(r.id, "name")} data-testid={`${p}-name-${r.id}`} />
                  </Field>
                  <Field label={TL("2nd class")}>
                    {Array.isArray(class2Options) ? (
                      <select className={II} value={r.subcategory || ""} onChange={(e) => persistNow(r.id, "subcategory", e.target.value)} data-testid={`${p}-class2-${r.id}`}>
                        <option value="">—</option>
                        {class2Options.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <input className={II} value={r.subcategory || ""} placeholder={TL("e.g. Residential")} onChange={(e) => setField(r.id, "subcategory", e.target.value)} onBlur={() => persist(r.id, "subcategory")} data-testid={`${p}-class2-${r.id}`} />
                    )}
                  </Field>
                  <Field label={
                    <span className="flex items-center gap-1">
                      <button type="button" onClick={() => setYearMode((prev) => ({ ...prev, [r.id]: "made" }))} data-testid={`${p}-yearmode-made-${r.id}`} className={`rounded px-1 ${ym === "made" ? "bg-[var(--primary)] text-white" : "opacity-55"}`}>{madeLabel}</button>
                      <button type="button" onClick={() => setYearMode((prev) => ({ ...prev, [r.id]: "bought" }))} data-testid={`${p}-yearmode-bought-${r.id}`} className={`rounded px-1 ${ym === "bought" ? "bg-[var(--primary)] text-white" : "opacity-55"}`}>{TL("Bought")}</button>
                    </span>
                  }>
                    <input className={II} type="number" placeholder={`Year ${ym === "made" ? madeLabel.toLowerCase() : "bought"}`} value={r[yearKey] || ""} onChange={(e) => setField(r.id, yearKey, e.target.value)} onBlur={() => persist(r.id, yearKey)} data-testid={`${p}-year-${r.id}`} />
                    {r[otherKey] ? <span className="text-[9px] opacity-45">{ym === "made" ? "bought" : madeLabel.toLowerCase()} {r[otherKey]}</span> : null}
                  </Field>
                  <Field label="Last appraised ($)">
                    <input className={`${II} tabular-nums`} type="number" value={r.last_appraised || ""} onChange={(e) => setField(r.id, "last_appraised", e.target.value)} onBlur={() => persist(r.id, "last_appraised")} data-testid={`${p}-appraised-${r.id}`} />
                  </Field>
                  <Field label="Annual depreciation ($)">
                    <input className={`${II} tabular-nums`} type="number" value={r.annual_dep || ""} onChange={(e) => setField(r.id, "annual_dep", e.target.value)} onBlur={() => persist(r.id, "annual_dep")} data-testid={`${p}-dep-${r.id}`} />
                  </Field>
                  <div>
                    <span className={LL}>{TL("Book value")}</span>
                    <div className="rounded border border-black/10 bg-black/[0.03] px-1.5 py-1 text-[11px] font-semibold tabular-nums dark:border-white/10 dark:bg-white/[0.04]" data-testid={`${p}-book-${r.id}`}>{money(bookValueOf(r))}</div>
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <AppraisalButton holding={r} reload={reload} />
                    <AttachmentsButton coll="holdings" recordId={r.id} compact />
                    <Icon size={14} className="opacity-40" />
                    <button onClick={() => del(raw)} data-testid={`${p}-del-${r.id}`} className="opacity-50 hover:text-red-500 hover:opacity-100"><Trash2 size={14} /></button>
                  </div>
                </div>

                {/* ===== Lower line ===== */}
                <div className="mt-2 grid items-end gap-2 border-t border-black/5 pt-2 dark:border-white/5 md:grid-cols-5">
                  <Field label={TL("Status")}>
                    <select className={II} value={r.mp_status || ""} onChange={(e) => persistNow(r.id, "mp_status", e.target.value)} data-testid={`${p}-status-${r.id}`}>
                      <option value="">—</option>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Annual outlay ($)">
                    <input className={`${II} tabular-nums`} type="number" value={r.annual_outlay || ""} onChange={(e) => setField(r.id, "annual_outlay", e.target.value)} onBlur={() => persist(r.id, "annual_outlay")} data-testid={`${p}-outlay-${r.id}`} />
                  </Field>
                  <Field label={TL("Intent")}>
                    <select className={II} value={r.intent || ""} onChange={(e) => persistNow(r.id, "intent", e.target.value)} data-testid={`${p}-intent-${r.id}`}>
                      <option value="">—</option>
                      {intents.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Income ($)">
                    <input className={`${II} tabular-nums`} type="number" value={r.income_annual || ""} onChange={(e) => setField(r.id, "income_annual", e.target.value)} onBlur={() => persist(r.id, "income_annual")} data-testid={`${p}-income-${r.id}`} />
                  </Field>
                  <div>
                    <span className={LL}>{TL("Net")}</span>
                    <div className={`rounded border border-black/10 bg-black/[0.03] px-1.5 py-1 text-[11px] font-semibold tabular-nums dark:border-white/10 dark:bg-white/[0.04] ${netOf(r) >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid={`${p}-net-${r.id}`}>{netOf(r) >= 0 ? "+" : "−"}{money(Math.abs(netOf(r)))}</div>
                  </div>
                </div>

                {/* ===== Settle strip ===== */}
                <div className="mt-2 flex flex-wrap items-end gap-2 border-t border-black/5 pt-2 dark:border-white/5" data-testid={`${p}-settle-${r.id}`}>
                  <span className="text-[9px] uppercase tracking-wide opacity-55">{TL("Settle to Currencida")}</span>
                  <select className={`${II} w-auto min-w-[150px]`} value={r.settle_account || ""} onChange={(e) => persistNow(r.id, "settle_account", e.target.value)} data-testid={`${p}-settle-account-${r.id}`}>
                    <option value="">{TL("— account —")}</option>
                    {pools.map((pool) => <option key={pool.id} value={pool.id}>{pool.name || pool.id}{pool.balance != null ? ` (${pool.balance})` : ""}</option>)}
                  </select>
                  <label className="block"><span className={LL}>{TL("Frequency")}</span>
                    <select className={`${II} w-auto`} value={r.settle_freq || "Annual"} onChange={(e) => persistNow(r.id, "settle_freq", e.target.value)} data-testid={`${p}-settle-freq-${r.id}`}>
                      {Object.keys(FREQS).map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </label>
                  <button type="button" onClick={() => adjust(r.settle_account, "debit", sliceOf(r, "annual_outlay"), "mobility_outlay", { name: r.name, class: r.subcategory, freq: r.settle_freq || "Annual" })} data-testid={`${p}-run-outlay-${r.id}`} className="rounded bg-red-500/15 px-2 py-1 text-[10px] font-semibold text-red-700 hover:bg-red-500/25 disabled:opacity-40 dark:text-red-400" disabled={!(sliceOf(r, "annual_outlay") > 0)}>Run outlay (−{money(sliceOf(r, "annual_outlay"))})</button>
                  <button type="button" onClick={() => adjust(r.settle_account, "credit", sliceOf(r, "income_annual"), "mobility_income", { name: r.name, class: r.subcategory, freq: r.settle_freq || "Annual" })} data-testid={`${p}-run-income-${r.id}`} className="rounded bg-green-500/15 px-2 py-1 text-[10px] font-semibold text-green-700 hover:bg-green-500/25 disabled:opacity-40 dark:text-green-400" disabled={!(sliceOf(r, "income_annual") > 0)}>Run income (+{money(sliceOf(r, "income_annual"))})</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
